import { useEffect, useMemo, useRef, useState } from "react";
import { PATHURLDOMAIN } from "../../constantes/constantes";
import { useCustomScrollbar } from "../../hooks/useCustomScrollbar";
import { useInitialResults } from "../../hooks/useInitialResults";
import { useCalculatedHeight } from "../../hooks/useCalculatedHeight";
import TurnContent from "./turnContent";
import CardResults from "./cardResults";
import NotesSkeletonLoader from "../skeleton/notesSkeletonLoader";
import "./abstract.sass";
import "./meta.sass";
import "./cardResults.sass";

const GAP = 10;
const PADDING = 30;
const BUTTON_HEIGHT = 50;

/**
 * Renderiza el historial conversacional con el layout original a 2 columnas:
 *  - Izquierda: `summary-card` único con todos los turnos (estilo ChatGPT).
 *  - Derecha: `list-wrapper-results` con las notas relacionadas del turno más
 *    reciente (las recomendaciones acompañan a la última pregunta).
 */
export default function Results({ conversation, isMobile, onNewConversation }) {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const { containerRef, thumbRef, trackRef, handleMouseDown, handleTrackClick, scrollToBottom } =
    useCustomScrollbar(setHasOverflow, setIsAtBottom);

  const parentBase = useMemo(
    () => (PATHURLDOMAIN ? PATHURLDOMAIN.replace(/\/+$/, "") : ""),
    []
  );

  // Auto-scroll al final cuando llega un turno nuevo o cambia el contenido del último
  const lastTurn = conversation[conversation.length - 1];
  const lastTurnId = lastTurn?.id;
  const lastResumeLen = lastTurn?.resume?.length || 0;
  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [lastTurnId, lastResumeLen, containerRef]);

  // ---- Panel derecho: notas del último turno ----
  const [showAll, setShowAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const gridRef = useRef(null);
  const INITIAL_RESULTS = useInitialResults();

  // Reset del expandido cuando cambia el turno (nueva pregunta -> nuevas notas)
  useEffect(() => {
    setShowAll(false);
    setIsTransitioning(false);
  }, [lastTurnId]);

  const lastHits = Array.isArray(lastTurn?.hits) ? lastTurn.hits : [];
  const sortedData = useMemo(
    () => [...lastHits].sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date)),
    [lastHits]
  );
  const visibleData = useMemo(
    () => (showAll ? sortedData : sortedData.slice(0, INITIAL_RESULTS)),
    [showAll, sortedData, INITIAL_RESULTS]
  );

  const collapsedHeight = useCalculatedHeight(gridRef, INITIAL_RESULTS, {
    gap: GAP,
    padding: PADDING,
    extraHeight: BUTTON_HEIGHT,
  });

  const hasNotes = sortedData.length > 0;
  const hasMoreResults = sortedData.length > INITIAL_RESULTS;
  const notesAreLoading = lastTurn && !lastTurn.notesReady;

  // El maxHeight debe ser siempre el mismo (altura de INITIAL_RESULTS cards).
  // La diferencia es que cuando showAll=true, el .grid-news hace scroll interno.
  const wrapperStyle = isMobile
    ? {}
    : { maxHeight: collapsedHeight ? `${collapsedHeight}px` : "auto" };

  const handleToggle = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowAll(prev => !prev);
      setIsTransitioning(false);
    }, 300);
  };

  if (!conversation || conversation.length === 0) return null;

  return (
    <div className="results-container">
      <div className="container-full-results">

        {/* IZQUIERDA: card único con toda la conversación */}
        <div className="summary-card-wrapper">
          {/* Botón de nueva conversación en esquina superior derecha */}
          {/* <button
            className="btn-new-conversation"
            onClick={onNewConversation}
            title="Iniciar nueva conversación"
            aria-label="Borrar historial e iniciar nueva conversación"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            <span className="btn-new-conversation-text">Nueva conversación</span>
          </button> */}

          <section
            className="summary-card conversation-card"
            ref={containerRef}
            aria-label="Historial de conversación"
          >
            <div className="line-upsale"></div>
            <div className="dots_border"></div>

            {conversation.map((turn, idx) => (
              <TurnContent
                key={turn.id}
                turn={turn}
                parentBase={parentBase}
                isLast={idx === conversation.length - 1}
              />
            ))}
          </section>

          {/* Botón flotante: ir al final cuando hay overflow */}
          <div className={`summary-scroll-button-wrapper ${!hasOverflow || isAtBottom ? "hidden" : ""}`}>
            <button
              className="summary-scroll-to-bottom"
              onClick={scrollToBottom}
              title="Ir al final"
              aria-label="Desplazarse al final del contenido"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>

          {/* Scrollbar personalizada compartida */}
          <div className="summary-scrollbar-track" ref={trackRef} onClick={handleTrackClick}>
            <div
              className="summary-scrollbar-thumb"
              ref={thumbRef}
              onMouseDown={handleMouseDown}
            />
          </div>
        </div>

        {/* DERECHA: notas relacionadas del turno más reciente (o skeleton si están cargando) */}
        {notesAreLoading ? (
          <NotesSkeletonLoader count={INITIAL_RESULTS} />
        ) : hasNotes ? (
          <section
            className={`list-wrapper-results${showAll ? " expanded" : ""}`}
            style={wrapperStyle}
          >
            <h2 className="list-wrapper-title">Te puede interesar:</h2>

            <div
              ref={gridRef}
              id="resultadosUTV"
              className="grid-news"
              role="region"
              aria-label="Resultados de búsqueda"
            >
              <CardResults data={visibleData} query={lastTurn?.query} />
            </div>

            {hasMoreResults && (
              <button
                className={`btn-show-more${showAll ? " btn-close" : ""}${isTransitioning ? " transitioning" : ""}`}
                onClick={handleToggle}
                aria-expanded={showAll}
                aria-controls="resultadosUTV"
              >
                {showAll ? (
                  <svg className="icon-close" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  "Mostrar más"
                )}
              </button>
            )}
          </section>
        ) : (
          <aside className="list-wrapper-results notes-placeholder" aria-label="Sin recomendaciones disponibles" />
        )}
      </div>
    </div>
  );
}
